import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import type { KanbanColumnDTO } from "@unik/shared";
import { api, ApiClientError } from "../lib/api";

const PRESET_COLORS = [
  { label: "Azul Safira", value: "#3b82f6" },
  { label: "Violeta Nobre", value: "#8b5cf6" },
  { label: "Pink Carmim", value: "#ec4899" },
  { label: "Ouro Unik", value: "#c2a06b" },
  { label: "Verde Esmeralda", value: "#10b981" },
  { label: "Laranja Âmbar", value: "#f59e0b" },
  { label: "Rubi Intenso", value: "#e11d48" },
  { label: "Grafite Neutro", value: "#64748b" },
];

interface ColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  columnToEdit?: KanbanColumnDTO | null;
}

export function ColumnModal({
  isOpen,
  onClose,
  onSaved,
  columnToEdit,
}: ColumnModalProps) {
  const isEditing = Boolean(columnToEdit);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#3b82f6");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (columnToEdit) {
      setLabel(columnToEdit.label);
      setColor(columnToEdit.color);
    } else {
      setLabel("");
      setColor("#3b82f6");
    }
    setError(null);
  }, [columnToEdit, isOpen]);

  if (!isOpen) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) {
      setError("Informe o nome do status.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (isEditing && columnToEdit) {
        await api.patch(`/kanban/columns/${columnToEdit.id}`, {
          label: label.trim(),
          color,
        });
      } else {
        await api.post("/kanban/columns", {
          label: label.trim(),
          color,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Não foi possível salvar o status.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-[var(--radius-card)] border border-border-default bg-surface-raised p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-text-tertiary hover:text-text-primary"
          aria-label="Fechar"
        >
          ✕
        </button>

        <div className="flex items-center gap-3">
          <span
            className="h-4 w-4 shrink-0 rounded-full shadow-sm"
            style={{ backgroundColor: color }}
          />
          <h2 className="type-h2 text-text-cream">
            {isEditing ? "Editar Status" : "Novo Status no Kanban"}
          </h2>
        </div>
        <p className="type-caption mt-1 text-text-secondary">
          {isEditing
            ? "Modifique o nome ou a cor de identificação da coluna."
            : "Adicione uma nova etapa personalizada no fluxo de clientes."}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5">
          <div>
            <label className="type-label block text-text-secondary mb-1">
              Nome do Status / Coluna
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Reunião de Kickoff, Aguardando Gravação..."
              className="w-full rounded-[var(--radius-control)] border border-border-default bg-surface-base px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="type-label block text-text-secondary mb-2">
              Cor de Destaque
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={`flex items-center gap-2 rounded-[var(--radius-control)] border p-2 text-xs transition-all ${
                    color === c.value
                      ? "border-gold bg-surface-elevated font-medium text-text-cream shadow-sm"
                      : "border-border-subtle bg-surface-base text-text-secondary hover:border-border-strong"
                  }`}
                >
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: c.value }}
                  />
                  <span className="truncate">{c.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="type-caption text-text-tertiary">Personalizada:</span>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-8 w-14 cursor-pointer rounded border border-border-default bg-surface-base p-0.5"
              />
              <span className="type-caption font-mono text-text-secondary">{color}</span>
            </div>
          </div>

          {error && (
            <p role="alert" className="type-caption text-danger">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[var(--radius-control)] border border-border-default px-4 py-2 text-xs font-medium text-text-secondary hover:bg-surface-elevated"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="liquid-glass-brand rounded-[var(--radius-control)] px-4 py-2 text-xs font-medium text-text-primary hover:brightness-110 disabled:opacity-50"
            >
              {loading ? "Salvando..." : isEditing ? "Salvar Alterações" : "Criar Status"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
